import React, { useEffect, useState, useCallback } from 'react';
import { fetchJson } from './api';
import { useTranslation } from 'react-i18next';

// ─── Palette (stessa di PianoDiPost / CoordinatorsPage) ─────────────────────
const GOLD        = '#d4a550';
const GOLD_DIM    = 'rgba(212,165,80,0.13)';
const GOLD_BORDER = 'rgba(212,165,80,0.28)';
const BORDER      = 'rgba(255,255,255,0.07)';
const TEXT        = '#e8e6df';
const TEXT_DIM    = 'rgba(232,230,223,0.5)';
const TEXT_MED    = 'rgba(232,230,223,0.78)';
const BG          = '#0d0d14';
const BG2         = '#13131f';
const BG3         = '#1a1a28';
const GREEN       = '#4ade80';
const GREEN_DIM   = 'rgba(74,222,128,0.13)';
const RED         = '#f87171';
const RED_DIM     = 'rgba(248,113,113,0.13)';
const BLUE        = '#60a5fa';
const BLUE_DIM    = 'rgba(96,165,250,0.13)';

// ─── Copia da backend/routes/roles-map.js — NON importare dal backend ────────
const ROLES_POSTPRODUZIONE = [
  'Assistente al Montaggio',
  'Autore al montaggio',
  'Colorist',
  'Coordinatore di Post Produzione',
  'Data Manager',
  'Editor Video / Montatore',
  'Editor Video / Montatore (Junior)',
  'Editor Video / Montatore (Senior)',
  'Finalizzatore',
  'Ingest',
  'Logger',
  'Motion Graphic Designer',
  'Postproduzione Audio',
  'Sound Designer / Montatore del Suono',
  'VFX Artist',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso, lang = 'it') {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString(
    lang === 'en' ? 'en-GB' : 'it-IT',
    { day: 'numeric', month: 'short', year: 'numeric' }
  );
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Componente pannello collegamento (inline nella card) ────────────────────
function CollegaPanel({ suggestion, onDone, onCancel }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';

  // Step a — ricerca programma
  const [programQuery, setProgramQuery]     = useState('');
  const [programResults, setProgramResults] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [searchLoading, setSearchLoading]   = useState(false);

  // Step b — edizioni
  const [editions, setEditions]             = useState([]);
  const [editionsLoading, setEditionsLoading] = useState(false);
  const [selectedEditions, setSelectedEditions] = useState([]); // array di id

  // Mini-form nuova edizione
  const [showAddEdition, setShowAddEdition] = useState(false);
  const [newLabel, setNewLabel]             = useState('');
  const [newYear, setNewYear]               = useState('');
  const [savingEdition, setSavingEdition]   = useState(false);

  // Step c — ruolo
  const suggestedRole = suggestion.ruolo && ROLES_POSTPRODUZIONE.includes(suggestion.ruolo)
    ? suggestion.ruolo
    : null;
  const [selectedRole, setSelectedRole] = useState(suggestedRole || '');

  // Stato submit
  const [submitting, setSubmitting]         = useState(false);
  const [results, setResults]               = useState(null); // {ok:[],dup:[],err:[]}

  const debouncedQuery = useDebounce(programQuery, 300);

  // Cerca programmi
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setProgramResults([]);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    fetchJson(`/cv/programs?q=${encodeURIComponent(debouncedQuery)}`)
      .then(data => { if (!cancelled) setProgramResults(data?.programs || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Carica edizioni quando si seleziona un programma
  const selectProgram = useCallback(async (prog) => {
    setSelectedProgram(prog);
    setProgramQuery(prog.name);
    setProgramResults([]);
    setEditions([]);
    setSelectedEditions([]);
    setShowAddEdition(false);
    setEditionsLoading(true);
    try {
      const data = await fetchJson(`/cv/programs/${prog.id}/editions`);
      setEditions(data?.editions || []);
    } catch {
      setEditions([]);
    } finally {
      setEditionsLoading(false);
    }
  }, []);

  // Toggle selezione edizione
  const toggleEdition = (id) => {
    setSelectedEditions(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Salva nuova edizione
  const handleSaveEdition = async () => {
    if (!newLabel.trim() || !newYear) return;
    setSavingEdition(true);
    try {
      const data = await fetchJson('/cv/editions', {
        method: 'POST',
        body: JSON.stringify({
          program_id: selectedProgram.id,
          edition_label: newLabel.trim(),
          edition_no: parseInt(newLabel.trim(), 10) || null,
          year: parseInt(newYear, 10),
        }),
      });
      const newEd = data?.edition || data;
      setEditions(prev => [...prev, newEd]);
      setSelectedEditions(prev => [...prev, newEd.id]);
      setNewLabel('');
      setNewYear('');
      setShowAddEdition(false);
    } catch {
      // ignora — l'edizione non viene aggiunta
    } finally {
      setSavingEdition(false);
    }
  };

  // Submit — collega ogni edizione selezionata
  const handleCollega = async () => {
    if (!selectedProgram || selectedEditions.length === 0 || !selectedRole) return;
    setSubmitting(true);
    const ok = [], dup = [], err = [];
    const isSingle = selectedEditions.length === 1;

    for (const edId of selectedEditions) {
      try {
        const body = {
          edition_id: edId,
          role: selectedRole,
          source_maestranza_id: suggestion.id,
        };
        if (isSingle) {
          if (suggestion.data_inizio_contratto) body.self_start = suggestion.data_inizio_contratto;
          if (suggestion.data_fine_contratto)   body.self_end   = suggestion.data_fine_contratto;
        }
        await fetchJson('/cv/credits', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        ok.push(edId);
      } catch (e) {
        if (e.message && e.message.includes('già esistente')) {
          dup.push(edId);
        } else {
          // Prova a rilevare 409 da status nel messaggio
          try {
            const parsed = JSON.parse(e.message);
            if (parsed?.status === 409) { dup.push(edId); continue; }
          } catch {}
          err.push(edId);
        }
      }
    }

    setResults({ ok, dup, err });
    setSubmitting(false);

    // Se almeno un credito è andato a buon fine (o era già collegato), ricarichiamo
    if (ok.length > 0 || dup.length > 0) {
      setTimeout(() => onDone(), 1200);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      marginTop: 12,
      background: BG3,
      border: `1px solid ${GOLD_BORDER}`,
      borderRadius: 12,
      padding: '20px 22px',
    }}>

      {/* Step a — Cerca programma */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: GOLD,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
          {t('crediti.label_cerca_programma')}
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={programQuery}
            onChange={e => { setProgramQuery(e.target.value); setSelectedProgram(null); }}
            placeholder={t('crediti.ph_cerca_programma')}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: BG2, border: `1px solid ${BORDER}`,
              borderRadius: 8, padding: '9px 12px',
              color: TEXT, fontSize: 13, outline: 'none',
            }}
          />
          {searchLoading && (
            <span style={{ position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)', color: TEXT_DIM, fontSize: 11 }}>
              ...
            </span>
          )}
          {programResults.length > 0 && !selectedProgram && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
              background: BG2, border: `1px solid ${BORDER}`, borderRadius: 8,
              marginTop: 2, maxHeight: 220, overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}>
              {programResults.map(p => (
                <div
                  key={p.id}
                  onClick={() => selectProgram(p)}
                  style={{
                    padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: TEXT,
                    display: 'flex', alignItems: 'center', gap: 8,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = BG3}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ flex: 1 }}>{p.name}</span>
                  {p.is_verified && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
                    }}>
                      {t('crediti.badge_verificato')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedProgram && (
          <div style={{
            marginTop: 8, display: 'flex', alignItems: 'center', gap: 8,
            background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`,
            borderRadius: 8, padding: '6px 12px',
          }}>
            <span style={{ color: GOLD, fontSize: 13, fontWeight: 600, flex: 1 }}>
              {selectedProgram.name}
            </span>
            {selectedProgram.is_verified && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
              }}>
                {t('crediti.badge_verificato')}
              </span>
            )}
            <button
              onClick={() => { setSelectedProgram(null); setProgramQuery(''); setEditions([]); setSelectedEditions([]); }}
              style={{ background: 'none', border: 'none', color: TEXT_DIM, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Step b — Edizioni */}
      {selectedProgram && (
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: TEXT_DIM,
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
            {t('crediti.label_seleziona_edizioni')}
          </label>

          {editionsLoading && (
            <div style={{ color: TEXT_DIM, fontSize: 12, padding: '8px 0' }}>
              {t('crediti.loading_edizioni')}
            </div>
          )}

          {!editionsLoading && editions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {editions.map(ed => {
                const checked = selectedEditions.includes(ed.id);
                return (
                  <label key={ed.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer', padding: '7px 10px', borderRadius: 8,
                    background: checked ? GOLD_DIM : BG2,
                    border: `1px solid ${checked ? GOLD_BORDER : BORDER}`,
                    fontSize: 13, color: checked ? GOLD : TEXT_MED,
                    transition: 'all 0.15s',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEdition(ed.id)}
                      style={{ accentColor: GOLD, width: 14, height: 14, flexShrink: 0 }}
                    />
                    <span style={{ flex: 1 }}>
                      {ed.edition_label
                        ? `${t('crediti.edizione_prefix')} ${ed.edition_label}`
                        : ed.edition_no
                          ? `${t('crediti.edizione_prefix')} ${ed.edition_no}`
                          : `ID ${ed.id}`}
                      {ed.year ? ` · ${ed.year}` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Bottone aggiungi edizione */}
          {!editionsLoading && (
            <button
              onClick={() => setShowAddEdition(v => !v)}
              style={{
                background: 'none', border: `1px dashed ${BORDER}`,
                borderRadius: 8, padding: '7px 14px',
                color: TEXT_DIM, fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>+</span>
              {t('crediti.aggiungi_edizione_btn')}
            </button>
          )}

          {/* Mini-form nuova edizione */}
          {showAddEdition && (
            <div style={{
              marginTop: 10, background: BG2,
              border: `1px solid ${BORDER}`, borderRadius: 10,
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <label style={{ display: 'block', fontSize: 10, color: TEXT_DIM,
                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
                    {t('crediti.label_edition_label')}
                  </label>
                  <input
                    type="text"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    placeholder={t('crediti.ph_edition_label')}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: BG3, border: `1px solid ${BORDER}`,
                      borderRadius: 6, padding: '7px 10px',
                      color: TEXT, fontSize: 13, outline: 'none',
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 100 }}>
                  <label style={{ display: 'block', fontSize: 10, color: TEXT_DIM,
                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
                    {t('crediti.label_year')}
                  </label>
                  <input
                    type="number"
                    value={newYear}
                    onChange={e => setNewYear(e.target.value)}
                    placeholder={t('crediti.ph_year')}
                    min={1990} max={2100}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: BG3, border: `1px solid ${BORDER}`,
                      borderRadius: 6, padding: '7px 10px',
                      color: TEXT, fontSize: 13, outline: 'none',
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleSaveEdition}
                  disabled={savingEdition || !newLabel.trim() || !newYear}
                  style={{
                    padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    background: savingEdition || !newLabel.trim() || !newYear
                      ? 'rgba(255,255,255,0.05)' : GREEN_DIM,
                    border: `1px solid ${GREEN}`, color: GREEN,
                    cursor: savingEdition || !newLabel.trim() || !newYear ? 'not-allowed' : 'pointer',
                  }}
                >
                  {savingEdition ? t('crediti.saving') : t('crediti.salva_edizione_btn')}
                </button>
                <button
                  onClick={() => { setShowAddEdition(false); setNewLabel(''); setNewYear(''); }}
                  style={{
                    padding: '7px 14px', borderRadius: 8, fontSize: 12,
                    background: 'none', border: `1px solid ${BORDER}`,
                    color: TEXT_DIM, cursor: 'pointer',
                  }}
                >
                  {t('crediti.annulla')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step c — Ruolo */}
      {selectedProgram && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: TEXT_DIM,
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
            {t('crediti.label_ruolo')}
          </label>
          {suggestedRole ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: BLUE_DIM, border: `1px solid rgba(96,165,250,0.3)`,
              borderRadius: 8, padding: '8px 12px',
            }}>
              <span style={{ color: BLUE, fontSize: 13, fontWeight: 600, flex: 1 }}>
                {suggestedRole}
              </span>
              <span style={{ fontSize: 10, color: BLUE, opacity: 0.7 }}>
                {t('crediti.ruolo_preselezionato')}
              </span>
            </div>
          ) : (
            <select
              value={selectedRole}
              onChange={e => setSelectedRole(e.target.value)}
              style={{
                width: '100%', background: BG2,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: '9px 12px', color: selectedRole ? TEXT : TEXT_DIM,
                fontSize: 13, outline: 'none', cursor: 'pointer',
              }}
            >
              <option value="">{t('crediti.ph_ruolo')}</option>
              {ROLES_POSTPRODUZIONE.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Step d — Conferma */}
      {selectedProgram && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={handleCollega}
            disabled={submitting || selectedEditions.length === 0 || !selectedRole}
            style={{
              padding: '10px 22px', borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: submitting || selectedEditions.length === 0 || !selectedRole
                ? 'rgba(255,255,255,0.04)' : GOLD_DIM,
              border: `1px solid ${GOLD}`, color: GOLD,
              cursor: submitting || selectedEditions.length === 0 || !selectedRole
                ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? t('crediti.collegando') : t('crediti.collega_btn')}
          </button>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 16px', borderRadius: 10, fontSize: 13,
              background: 'none', border: `1px solid ${BORDER}`,
              color: TEXT_DIM, cursor: 'pointer',
            }}
          >
            {t('crediti.annulla')}
          </button>
        </div>
      )}

      {/* Risultati */}
      {results && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {results.ok.length > 0 && (
            <div style={{
              background: GREEN_DIM, border: `1px solid rgba(74,222,128,0.3)`,
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: GREEN,
            }}>
              {t('crediti.result_ok', { count: results.ok.length })}
            </div>
          )}
          {results.dup.length > 0 && (
            <div style={{
              background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`,
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: GOLD,
            }}>
              {t('crediti.result_dup', { count: results.dup.length })}
            </div>
          )}
          {results.err.length > 0 && (
            <div style={{
              background: RED_DIM, border: `1px solid rgba(248,113,113,0.3)`,
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: RED,
            }}>
              {t('crediti.result_err', { count: results.err.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Card singola suggerimento ────────────────────────────────────────────────
function SuggestionCard({ suggestion, onLinked, onDismissed }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [open, setOpen] = useState(false);

  // Dismiss inline
  const [dismissStep, setDismissStep] = useState(0); // 0 = hidden, 1 = "Sicuro?", 2 = dismissing
  const [dismissError, setDismissError] = useState(null);

  // Confirm proposal
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  // Ruolo inline per card proposta (quando il ruolo non è in ROLES_POSTPRODUZIONE)
  const hasProposal = !!suggestion.proposed_match && !suggestion.multi_edition_hint;
  const pm = suggestion.proposed_match;
  const preselectedRole = suggestion.ruolo && ROLES_POSTPRODUZIONE.includes(suggestion.ruolo)
    ? suggestion.ruolo
    : null;
  const [inlineRole, setInlineRole] = useState(preselectedRole || '');

  const handleDone = useCallback(() => {
    setOpen(false);
    onLinked(1);
  }, [onLinked]);

  // ── Dismiss ──────────────────────────────────────────────────────────────
  const handleDismissClick = () => {
    if (dismissStep === 0) { setDismissStep(1); setDismissError(null); return; }
    if (dismissStep === 1) {
      // conferma → chiama API
      setDismissStep(2);
      fetchJson(`/cv/suggestions/${suggestion.id}/dismiss`, { method: 'POST' })
        .then(() => onDismissed(suggestion.id))
        .catch(() => {
          setDismissError(t('crediti.dismiss_error'));
          setDismissStep(1);
        });
    }
  };
  const handleDismissAbort = () => { setDismissStep(0); setDismissError(null); };

  // ── Confirm proposal ─────────────────────────────────────────────────────
  const handleConfirmProposal = async () => {
    if (!pm) return;
    const role = inlineRole;
    if (!role) return; // non dovrebbe succedere: il bottone è disabilitato se no ruolo
    setConfirming(true);
    setConfirmError(null);
    try {
      let editionId = pm.edition_id;
      // Se edition_to_create → prima crea l'edizione
      if (!editionId && pm.edition_to_create) {
        const res = await fetchJson('/cv/editions', {
          method: 'POST',
          body: JSON.stringify({
            program_id: pm.program_id,
            edition_label: pm.edition_to_create.edition_label,
          }),
        });
        editionId = res?.edition?.id || res?.id;
      }
      const body = {
        edition_id: editionId,
        role,
        source_maestranza_id: suggestion.id,
      };
      if (suggestion.data_inizio_contratto) body.self_start = suggestion.data_inizio_contratto;
      if (suggestion.data_fine_contratto)   body.self_end   = suggestion.data_fine_contratto;
      const res = await fetchJson('/cv/credits', { method: 'POST', body: JSON.stringify(body) });
      onLinked(res?.created ?? 1);
    } catch (e) {
      setConfirmError(t('crediti.confirm_error'));
    } finally {
      setConfirming(false);
    }
  };

  // ── Stile card ────────────────────────────────────────────────────────────
  const isProposalCard = hasProposal;
  const cardBorderColor = open
    ? GOLD_BORDER
    : isProposalCard
      ? 'rgba(212,165,80,0.5)'
      : BORDER;
  const cardBoxShadow = isProposalCard && !open
    ? '0 0 0 1px rgba(212,165,80,0.18), 0 2px 16px rgba(212,165,80,0.07)'
    : 'none';

  const needsRoleSelect = isProposalCard && !preselectedRole;
  const confirmDisabled = confirming || (needsRoleSelect && !inlineRole);

  return (
    <div style={{
      background: BG2,
      border: `1px solid ${cardBorderColor}`,
      borderRadius: 12,
      padding: '16px 20px',
      transition: 'border-color 0.2s',
      boxShadow: cardBoxShadow,
      position: 'relative',
    }}>

      {/* ── Header card: content + dismiss button ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>

        {/* Testo produzione / badge */}
        <div style={{ flex: 1, minWidth: 180 }}>
          {/* Eyebrow */}
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: TEXT_DIM, marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span>{t('crediti.lavorato_su')}</span>
            {/* Badge multi-edizione */}
            {suggestion.multi_edition_hint && (
              <span style={{
                fontSize: 9, fontWeight: 800, padding: '1px 8px', borderRadius: 20,
                background: 'rgba(96,165,250,0.12)', border: `1px solid rgba(96,165,250,0.35)`,
                color: BLUE, textTransform: 'uppercase', letterSpacing: '0.07em',
              }}>
                {t('crediti.multi_edition_badge')}
              </span>
            )}
          </div>

          {/* Nome produzione */}
          <div style={{ color: TEXT, fontWeight: 600, fontSize: 15, marginBottom: 4, lineHeight: 1.3 }}>
            {suggestion.produzione || t('crediti.produzione_senza_nome')}
          </div>

          {/* Pill ruolo */}
          {suggestion.ruolo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{
                fontSize: 11, padding: '2px 10px', borderRadius: 20,
                background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
              }}>
                {suggestion.ruolo}
              </span>
            </div>
          )}
          {suggestion.ruoli && suggestion.ruoli.length > 0 && !suggestion.ruolo && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {suggestion.ruoli.map((r, i) => (
                <span key={i} style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 20,
                  background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
                }}>{r}</span>
              ))}
            </div>
          )}

          {/* Date */}
          <div style={{ color: TEXT_DIM, fontSize: 12, marginTop: 2 }}>
            {suggestion.data_inizio_contratto && (
              <span>
                {fmtDate(suggestion.data_inizio_contratto, lang)}
                {suggestion.data_fine_contratto && ` → ${fmtDate(suggestion.data_fine_contratto, lang)}`}
              </span>
            )}
          </div>
        </div>

        {/* Dismiss button + CTA */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
          {/* Dismiss inline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {dismissStep === 1 && (
              <>
                <span style={{ fontSize: 11, color: RED, fontWeight: 700 }}>
                  {t('crediti.dismiss_confirm')}
                </span>
                <button
                  onClick={handleDismissClick}
                  disabled={dismissStep === 2}
                  style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 8, cursor: 'pointer',
                    background: RED_DIM, border: `1px solid ${RED}`, color: RED, fontWeight: 700,
                  }}
                >
                  {t('crediti.dismiss_yes')}
                </button>
                <button
                  onClick={handleDismissAbort}
                  style={{
                    fontSize: 11, padding: '3px 8px', borderRadius: 8, cursor: 'pointer',
                    background: 'none', border: `1px solid ${BORDER}`, color: TEXT_DIM,
                  }}
                >
                  {t('crediti.dismiss_no')}
                </button>
              </>
            )}
            {dismissStep === 2 && (
              <span style={{ fontSize: 11, color: TEXT_DIM }}>...</span>
            )}
            {dismissStep === 0 && (
              <button
                onClick={handleDismissClick}
                title={t('crediti.dismiss')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: TEXT_DIM, fontSize: 16, lineHeight: 1, padding: '2px 4px',
                  borderRadius: 6, opacity: 0.6,
                  transition: 'opacity 0.15s, color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = RED; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = TEXT_DIM; }}
              >
                ×
              </button>
            )}
          </div>

          {/* CTA principale — solo se non aperto */}
          {!open && !hasProposal && (
            <button
              onClick={() => setOpen(true)}
              style={{
                flexShrink: 0, padding: '8px 16px', borderRadius: 10,
                background: GOLD_DIM, border: `1px solid ${GOLD}`,
                color: GOLD, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {t('crediti.collega_catalogo_btn')}
            </button>
          )}
        </div>
      </div>

      {/* ── Errore dismiss ── */}
      {dismissError && (
        <div style={{
          marginTop: 8, fontSize: 11, color: RED,
          background: RED_DIM, borderRadius: 6, padding: '5px 10px',
          border: `1px solid rgba(248,113,113,0.3)`,
        }}>
          {dismissError}
        </div>
      )}

      {/* ── Sezione proposta (proposed_match) ── */}
      {hasProposal && !open && (
        <div style={{
          marginTop: 14,
          background: 'rgba(212,165,80,0.06)',
          border: `1px solid rgba(212,165,80,0.22)`,
          borderRadius: 10,
          padding: '12px 16px',
        }}>
          {/* Label proposta */}
          <div style={{
            fontSize: 11, fontWeight: 700, color: GOLD,
            letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6,
          }}>
            {pm.edition_to_create
              ? t('crediti.proposed_match_new_edition', {
                  program: pm.program_name,
                  edition: pm.edition_to_create.edition_label,
                })
              : t('crediti.proposed_match_label', {
                  program: pm.program_name,
                  edition: pm.edition_label,
                })
            }
          </div>

          {/* Selezione ruolo inline (solo se il ruolo non è già in lista) */}
          {needsRoleSelect && (
            <div style={{ marginBottom: 10 }}>
              <select
                value={inlineRole}
                onChange={e => setInlineRole(e.target.value)}
                style={{
                  width: '100%', background: BG2,
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  padding: '7px 10px', color: inlineRole ? TEXT : TEXT_DIM,
                  fontSize: 12, outline: 'none', cursor: 'pointer',
                }}
              >
                <option value="">{t('crediti.role_select_placeholder')}</option>
                {ROLES_POSTPRODUZIONE.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          {/* Bottoni: Conferma / No, scegli manualmente */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={handleConfirmProposal}
              disabled={confirmDisabled}
              style={{
                padding: '7px 18px', borderRadius: 9, fontSize: 12, fontWeight: 700,
                background: confirmDisabled ? 'rgba(255,255,255,0.04)' : GOLD_DIM,
                border: `1px solid ${confirmDisabled ? BORDER : GOLD}`,
                color: confirmDisabled ? TEXT_DIM : GOLD,
                cursor: confirmDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {confirming ? t('crediti.confirming') : t('crediti.confirm_proposal')}
            </button>
            <button
              onClick={() => setOpen(true)}
              style={{
                padding: '7px 14px', borderRadius: 9, fontSize: 12,
                background: 'none', border: `1px solid ${BORDER}`,
                color: TEXT_DIM, cursor: 'pointer',
              }}
            >
              {t('crediti.choose_manually')}
            </button>
          </div>

          {/* Errore conferma proposta */}
          {confirmError && (
            <div style={{
              marginTop: 8, fontSize: 11, color: RED,
              background: RED_DIM, borderRadius: 6, padding: '5px 10px',
              border: `1px solid rgba(248,113,113,0.3)`,
            }}>
              {confirmError}
            </div>
          )}
        </div>
      )}

      {/* ── Panel collegamento manuale ── */}
      {open && (
        <CollegaPanel
          suggestion={suggestion}
          onDone={handleDone}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// ─── BatchConfirmPanel ────────────────────────────────────────────────────────
function BatchConfirmPanel({ batchItems, onDone, onClose }) {
  const { t } = useTranslation();
  const [going, setGoing]       = useState(false);
  const [result, setResult]     = useState(null); // {created, skipped}
  const [batchError, setBatchError] = useState(null);

  const handleBatch = async () => {
    if (going) return;
    setGoing(true);
    setBatchError(null);
    try {
      const items = batchItems.map(s => {
        const body = {
          edition_id: s.proposed_match.edition_id,
          role: s._resolvedRole,
          source_maestranza_id: s.id,
        };
        if (s.data_inizio_contratto) body.self_start = s.data_inizio_contratto;
        if (s.data_fine_contratto)   body.self_end   = s.data_fine_contratto;
        return body;
      });
      const res = await fetchJson('/cv/credits/batch', {
        method: 'POST',
        body: JSON.stringify({ items }),
      });
      const created = res?.created ?? 0;
      setResult({ created, skipped: res?.skipped ?? 0 });
      onDone(created);
    } catch {
      setBatchError(t('crediti.batch_error'));
    } finally {
      setGoing(false);
    }
  };

  return (
    <div style={{
      marginBottom: 24,
      background: 'rgba(212,165,80,0.05)',
      border: `1px solid rgba(212,165,80,0.3)`,
      borderRadius: 14,
      padding: '18px 22px',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: GOLD,
        letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12,
      }}>
        {t('crediti.batch_summary_title')}
      </div>

      {/* Riepilogo compatto */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {batchItems.map(s => (
          <div key={s.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            background: BG3, borderRadius: 8, padding: '7px 12px',
            border: `1px solid ${BORDER}`,
          }}>
            <span style={{ color: TEXT, fontSize: 13, flex: 1, minWidth: 120 }}>
              {s.proposed_match.program_name}
            </span>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 20,
              background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
            }}>
              {s.proposed_match.edition_label}
            </span>
            <span style={{ fontSize: 11, color: TEXT_DIM }}>
              {s._resolvedRole}
            </span>
          </div>
        ))}
      </div>

      {/* Esito */}
      {result && !batchError && (
        <div style={{
          marginBottom: 12, background: GREEN_DIM, border: `1px solid rgba(74,222,128,0.3)`,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, color: GREEN,
        }}>
          {result.created === 0 && result.skipped === 0
            ? t('crediti.batch_result_zero')
            : t('crediti.batch_result', { created: result.created, skipped: result.skipped })}
        </div>
      )}
      {batchError && (
        <div style={{
          marginBottom: 12, background: RED_DIM, border: `1px solid rgba(248,113,113,0.3)`,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, color: RED,
        }}>
          {batchError}
        </div>
      )}

      {/* Bottoni */}
      <div style={{ display: 'flex', gap: 8 }}>
        {!result && (
          <button
            onClick={handleBatch}
            disabled={going}
            style={{
              padding: '9px 20px', borderRadius: 10, fontSize: 12, fontWeight: 700,
              background: going ? 'rgba(255,255,255,0.04)' : GOLD_DIM,
              border: `1px solid ${going ? BORDER : GOLD}`,
              color: going ? TEXT_DIM : GOLD,
              cursor: going ? 'not-allowed' : 'pointer',
            }}
          >
            {going ? t('crediti.batch_going') : t('crediti.batch_go')}
          </button>
        )}
        <button
          onClick={onClose}
          style={{
            padding: '9px 16px', borderRadius: 10, fontSize: 12,
            background: 'none', border: `1px solid ${BORDER}`,
            color: TEXT_DIM, cursor: 'pointer',
          }}
        >
          {t('crediti.batch_cancel')}
        </button>
      </div>
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function MieiCreditiPage() {
  const { t } = useTranslation();

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [linkedCount, setLinkedCount] = useState(0);
  const [showBatch, setShowBatch]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJson('/cv/me/suggestions');
      setSuggestions(data?.suggestions || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLinked = useCallback((created = 1) => {
    setLinkedCount(c => c + created);
    load();
  }, [load]);

  // Rimuove localmente una card dopo dismiss (senza ricaricare tutta la lista)
  const handleDismissed = useCallback((id) => {
    setSuggestions(prev => prev.filter(s => s.id !== id));
  }, []);

  // Calcola le card eleggibili per batch:
  // - proposed_match non null
  // - proposed_match.edition_id esiste (non edition_to_create)
  // - multi_edition_hint === false/falsy
  // - il ruolo è già in ROLES_POSTPRODUZIONE (o è preselezionabile)
  const batchEligible = suggestions.filter(s =>
    s.proposed_match &&
    s.proposed_match.edition_id &&
    !s.multi_edition_hint &&
    (s.ruolo && ROLES_POSTPRODUZIONE.includes(s.ruolo))
  ).map(s => ({
    ...s,
    _resolvedRole: s.ruolo,
  }));

  const showBatchBtn = batchEligible.length >= 2;

  const handleBatchDone = useCallback((created = 0) => {
    setLinkedCount(c => c + created);
    load();
  }, [load]);

  return (
    <div style={{ minHeight: '100vh', background: BG, padding: '32px 24px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 36 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: GOLD, marginBottom: 6,
            fontFamily: "'Kanit', sans-serif",
          }}>
            {t('crediti.eyebrow')}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, color: TEXT }}>
            {t('crediti.title')}
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: TEXT_DIM }}>
            {t('crediti.subtitle')}
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', color: TEXT_DIM, padding: 80, fontSize: 13 }}>
            {t('crediti.loading')}
          </div>
        )}

        {/* Errore */}
        {!loading && error && (
          <div style={{
            background: RED_DIM, border: '1px solid rgba(248,113,113,0.3)',
            borderRadius: 10, padding: 20, color: RED, fontSize: 13,
          }}>
            {t('crediti.error_prefix')}{error}
          </div>
        )}

        {/* Contenuto */}
        {!loading && !error && (
          <>
            {/* Sezione suggerimenti da collegare */}
            <section style={{ marginBottom: 48 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{
                  color: GOLD, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}>
                  {t('crediti.section_da_collegare')}
                </span>
                {suggestions.length > 0 && (
                  <span style={{
                    background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`,
                    borderRadius: 20, padding: '2px 10px',
                    fontSize: 11, color: GOLD, fontWeight: 700,
                  }}>
                    {suggestions.length}
                  </span>
                )}
                <div style={{ flex: 1, height: 1,
                  background: `linear-gradient(90deg,rgba(212,165,80,0.25),transparent)` }} />

                {/* Bottone batch (visibile solo se >= 2 proposte con edition_id già esistente) */}
                {showBatchBtn && !showBatch && (
                  <button
                    onClick={() => setShowBatch(true)}
                    style={{
                      flexShrink: 0, padding: '6px 14px', borderRadius: 10,
                      background: GOLD_DIM, border: `1px solid ${GOLD}`,
                      color: GOLD, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('crediti.batch_confirm_btn', { count: batchEligible.length })}
                  </button>
                )}
              </div>

              {/* Panel batch inline */}
              {showBatch && (
                <BatchConfirmPanel
                  batchItems={batchEligible}
                  onDone={handleBatchDone}
                  onClose={() => setShowBatch(false)}
                />
              )}

              {suggestions.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '40px 24px',
                  background: BG2, border: `1px solid ${BORDER}`,
                  borderRadius: 12,
                }}>
                  <div style={{ fontSize: 36, marginBottom: 14 }}>✓</div>
                  <div style={{ color: TEXT, fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
                    {t('crediti.empty_title')}
                  </div>
                  <div style={{ color: TEXT_DIM, fontSize: 13 }}>
                    {t('crediti.empty_sub')}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {suggestions.map(s => (
                    <SuggestionCard
                      key={s.id}
                      suggestion={s}
                      onLinked={handleLinked}
                      onDismissed={handleDismissed}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Sezione crediti collegati (contatore sessione) */}
            {linkedCount > 0 && (
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{
                    color: GREEN, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}>
                    {t('crediti.section_collegati')}
                  </span>
                  <div style={{ flex: 1, height: 1,
                    background: 'linear-gradient(90deg,rgba(74,222,128,0.25),transparent)' }} />
                </div>
                <div style={{
                  background: GREEN_DIM, border: `1px solid rgba(74,222,128,0.25)`,
                  borderRadius: 10, padding: '14px 18px',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <span style={{ fontSize: 20 }}>✓</span>
                  <span style={{ color: GREEN, fontSize: 13, fontWeight: 600 }}>
                    {t('crediti.collegati_in_sessione', { count: linkedCount })}
                  </span>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
